import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {
  BokkzAlgDataService,
  BokkzClient,
  BokkzTslDataService,
  ClientEntity,
  DossierItemEntity,
  IntermediaryEntity,
  WorkflowComponent,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormBuilder, FormGroup} from '@angular/forms';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {dispatchDeletedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {EMPTY, map, Subject, take, takeUntil} from 'rxjs';
import {CommonModule, DatePipe} from '@angular/common';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {ToastService} from "@snelstart/ui-toast";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {SharedUiTooltipModule} from "@snelstart/ui-tooltip";
import {environment} from "../../../../../../../apps/ventures/fiscaal/src/environments/environment";
import {SharedUiBadgeModule} from "@snelstart/ui-badge";
import {DialogService} from "@snelstart/ui-dialog";
import { SharedFeatureNavigationBarModule } from '@snelstart/feature-navigation-bar';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-tasks-list',
  templateUrl: './feature-tax-tasks-list.component.html',
  styleUrl: './feature-tax-tasks-list.component.scss',
    imports: [SharedUiTableModule, DatePipe, SharedUiIconModule, WorkflowComponent, CommonModule, SharedUiButtonModule, SharedUiTooltipModule, SharedUiBadgeModule, SharedFeatureNavigationBarModule]
})
export class FeatureTaxTasksListComponent implements OnInit, OnDestroy {

  @Output() public details = new EventEmitter<any>();
  protected tableConfig!: TableConfiguration<SearchCriteria, DossierItemEntity>;
  private loadingIds = new Set<string>();
  private destroy$ = new Subject<void>();
  public intermediaryEntity = {} as IntermediaryEntity;
  public dossierItemEntity = {} as DossierItemEntity;
  public client = {} as ClientEntity;
  public dossierItemFg = {} as FormGroup;
  public dialogVisible = false;
  public focusedElementId: string | null = null;
  public formStructure: any;
  public year = new Date().getFullYear();
  public type = '';
  public showTable = false;
  public result: string | undefined;

  constructor(
    public dataService: BokkzTslDataService,
    private bokkzClient: BokkzClient,
    public algDataService: BokkzAlgDataService,
    public dialogService: DialogService,
    public cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    public toastService: ToastService,
  ) {
    this.dossierItemFg = this.fb.group<DossierItemEntity>({
      id: null as unknown as number,
      clientId: null as unknown as number,
      title: null as unknown as string,
      creator: null as unknown as string,
      dateCreation: null as unknown as Date,
      dateDeadline: null as unknown as Date,
      hasDeadline: null as unknown as boolean,
      isChecked: null as unknown as boolean,
      opmerkingen: null as unknown as string,
      attachment: null as unknown as string[] | null as unknown as boolean,
      nameAttachment: null as unknown as string,
      checked: null as unknown as boolean,
      type: null as unknown as string,
      year: null as unknown as string,
    });
  }

  ngOnInit(): void {
    this.algDataService.intermediary.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.intermediaryEntity = value;
      setTimeout(() => {
        this.createTableConfig();
        this.showTable = true;
      }, 0);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createTableConfig(): void {
    this.tableConfig = TableConfiguration.create<SearchCriteria, DossierItemEntity>({
      identifier: 'id',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria: {skip: 0, take: 20} as SearchCriteria,
      search: (criteria: SearchCriteria) => {
        const skip = criteria.skip ?? 0;
        const take = criteria.take ?? 20;

        return this.bokkzClient.getTakenOverzicht().pipe(
          map(response => {
            const allResults = response ?? [];
            const pageResults = allResults.slice(skip, skip + take);
            return new SearchResult<DossierItemEntity>(pageResults, allResults.length);
          })
        );
      },
      fromDetailsMap: (result) => result,
      details: (entity) => {
        if (!entity) return EMPTY;
        const id = String(entity.id);

        if (this.loadingIds.has(id)) {
          return EMPTY;
        }

        this.loadingIds.add(id);

        return this.bokkzClient
          .getDossierItem(
            String(this.intermediaryEntity.sstPrimaryAdminId),
            id
          )
          .pipe(take(1))
          .subscribe({
            next: (dossierItem) => {
              if (dossierItem) {
                this.dossierItemEntity = entity;
                this.algDataService.updateWorkflowItem(dossierItem);
                this.details.next(entity.id);
              }
            },
            complete: () => {
              this.loadingIds.delete(id);
            },
            error: () => {
              this.loadingIds.delete(id);
            }
          });
      },
      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'list-check',
        mainText: $localize`Geen openstaande taken gevonden`,
        subText: '',
      }),
    });
  }

  public saveForm(record: DossierItemEntity, event: Event): void {
    event.stopPropagation();
    this.dialogService.showConfirmDeleteDialog({
      title: `Taak uit de takenlijst verwijderen`,
      body: $localize`Weet je zeker dat je deze taak afgerond hebt en uit de lijst wilt verwijderen?`,
      confirmText: 'Verwijderen',
      onConfirm: () => {
        return this.bokkzClient.postDossierItem(record.sstRelationId!, {
          ...record,
          checked: true,
        }).pipe(take(1))
      },
      onConfirmed: (result: DossierItemEntity) => {
        dispatchDeletedItemEvent({
          identifier: result.id!,
        });
        this.cdr.markForCheck();
      },
    });
  }

  goToClientDossier(record: DossierItemEntity, event: Event): void {
    event.stopPropagation();
    if (record.year) {
      this.algDataService.updateSelectedYear(Number(record.year));
    }
    if (record.type === 'IB') {
      window.open(environment.fiscaalBaseUrl + 'tax-income/' + record.sstRelationId, '_blank');
    }
    if (record.type === 'VPB') {
      window.open(environment.fiscaalBaseUrl + 'tax-corporate/' + record.sstRelationId, '_blank');
    }
    if (record.type === 'TSL') {
      window.open(environment.fiscaalBaseUrl + 'tax-allowance/' + record.sstRelationId, '_blank');
    }
    if (record.type === 'OB') {
      window.open(environment.fiscaalBaseUrl + 'tax-vat/' + record.sstRelationId, '_blank');
    }
    if (record.type === 'SB') {
      window.open(environment.fiscaalBaseUrl + 'tax-donation/' + record.sstRelationId, '_blank');
    }
    if (record.type === 'JR') {
      window.open(environment.fiscaalBaseUrl + 'pm-report/' + record.sstRelationId, '_blank');
    }
    if (record.type === 'WAGE') {
      window.open(environment.fiscaalBaseUrl + 'tax-wage/' + record.sstRelationId, '_blank');
    }
    if (record.type === 'DB') {
      window.open(environment.fiscaalBaseUrl + 'tax-dividend/' + record.sstRelationId, '_blank');
    }
  }
}
